// ════════════════════════════════════════════════════════════════════
// IEFriends.js — THE SOCIAL CHROME OF THE ILLEGAL EMPIRE
// ════════════════════════════════════════════════════════════════════
// MASTER COPY: gamemaster/engine/IEFriends.js — vendored byte-identical.
// Engine lives in ieSocial.js; this file is the face of it.
//
// ────────────────────────────────────────────────────────────────────
// THE ONE-LINE MOUNT
// ────────────────────────────────────────────────────────────────────
// A title joins the Empire's social layer with a single element:
//
//   <IESocialLayer uid={uid} name={playerName} game="terror" zone="The Plaza" />
//
// That renders the pill, the panel, the request badge and the invite
// toasts, and runs presence and the code index underneath. There is no
// second step, no provider to wrap, no state to thread. THIS IS THE
// POINT — a friend system that costs a title more than one line is a
// friend system that ships in two titles and rots in the rest, which
// is precisely what happened to ChallengeSystem.js (four copies,
// one call site, two of them shipped broken and never noticed).
//
// ────────────────────────────────────────────────────────────────────
// THE GLANCE LAW, APPLIED  (IE_CANON_PRIME §3.7b)
// ────────────────────────────────────────────────────────────────────
// The roster answers three questions before it answers anything else:
// who, are they on, and WHERE. Everything deeper — codes, blocking,
// the request queue — is one tap away and never in the way. A friends
// list you have to read is a friends list nobody opens mid-game.
//
// ────────────────────────────────────────────────────────────────────
// SELF-CONTAINED ON PURPOSE
// ────────────────────────────────────────────────────────────────────
// No theme import, no CDN asset, no icon package. Every title has its
// own theme.js and they disagree; the Mark is rendered in the Empire's
// own palette everywhere for the same reason canon gives THE MARK
// (§3.7b) — the player's identity should look the same in every room
// they carry it into. `accent` is the one knob a title may turn.
// ════════════════════════════════════════════════════════════════════

import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useFriends, useIEPresence, useMarkIndex, gameMeta, useSocialHidden,
         // AN INVITE CARRIES A SEAT — see ieSocial's invite() banner.
         matchCodeOf, inviteNoteOf, matchInviteHandler, publishSocialBridge,
         friendActionsFor, inviteOpener } from "./ieSocial";
import { useIEAuth } from "./ieIdentity";
import {
  MK, MK_CUT, mkCut, markSeal, injectMarkChrome,
  Kicker, Sheen, TopRule, MARK_ACCENTS,
} from "./markData";

// ════════════════════════════════════════════════════════════════════
// THE ROSTER WEARS THE MARK
// ════════════════════════════════════════════════════════════════════
// Not "a friends list styled to look a bit like the Mark" — the same
// tokens, the same cut corner, the same seal glyph, the same code, all
// imported from markData.js. §3.7b says THE MARK IS THE PLAYER, and a
// friends list is nothing but a column of players; rendering it in some
// other palette would have made the Mark a thing you visit rather than
// the thing you are.
//
// ⚠ WHY NOT MarkChip PER ROW. MarkChip calls useMark(uid), which opens
// a Firebase listener on that player's whole profile. Forty friends
// would be forty profile listeners for forty nameplates. THE GLANCE LAW
// says the card comes first and the depth is opt-in, so the row derives
// what it needs from PURE FUNCTIONS — markSeal(uid) and markCode(uid)
// cost nothing and cannot be wrong — and the full Mark opens on demand,
// one listener, only for the player actually being looked at.
// ⚠ THE ACCENT SPLIT. `accent` is the HOST TITLE's colour and it may
// tint container chrome only — the pill, the panel glow, a tab
// underline, a primary button. It may NEVER tint a seal, because the
// seal is derived from the uid and is the one thing on this panel that
// is nobody else's. Tinting it per-game would make your seal cyan on
// the Pier and violet in Terror, which is the exact failure §3.7b
// exists to prevent — committed inside the file whose header says it
// exists to prevent it. A person renders in Gilt everywhere.
const SEAL = MARK_ACCENTS.gold;

const SD = {
  ink:      "rgba(11,9,16,0.96)",   // MK.obsidian, with the panel's blur behind it
  inkSolid: MK.obsidian,
  line:     `${MK.edge}`,
  lineHot:  `${MK.edgeHot}`,
  text:     MK.bone,
  dim:      MK.ash,
  faint:    MK.smoke,
  gold:     MK.gold,
  goldHi:   MK.goldHi,
  green:    MK.verd,
  red:      MK.blood,
  font:     MK.sans,
  mono:     MK.mono,
  display:  MK.display,
};

// ⚠ THESE NUMBERS ARE MEASURED, NOT GUESSED. The first draft used 900
// "above room chrome, below a modal fight" — but Pierre has NOTHING
// between 400 and 9000, so 900 floated the friends pill on top of the
// ReadyUpModal's blackout during the 15-second party launch, the single
// most modal moment in the game.
//
// Pierre's real stack: portals 0-7 · proximity bubble 60 · wordmark 100
// · CrestHUD / log-out / SoloInvitePill 150 · PartyHUD 160 · dialogue,
// portal modal, D-pad, chat toggle 200 · party invite banner 220 ·
// invite flash 230 · zone chat 300 · ReadyUpModal 400 · the Mark 9000.
// ⚠ RAISED 2026-09-05 — THE STACK ABOVE IS PIERRE'S AND WAS NEVER
// RE-MEASURED FOR ANY OTHER TITLE. Terror's own chrome runs to 300, 400,
// 450, 500 and 620, and its StartMenu sits at 1100 — so an invite toast at
// 250 rendered UNDERNEATH most of the in-duel UI, in the one title where
// an invite now carries a duel to join. The layer already suppresses
// itself during a title's own modals (setSocialHidden), so it is entitled
// to sit above ordinary chrome; it stays well below a full-screen
// takeover, which is the one thing that must always win.
const Z_PILL  = 690;   // above room chrome, below any full-screen takeover
const Z_PANEL = 700;
const Z_TOAST = 710;

// The D-pad is 154px tall at bottom:24 on touch (PlayerEntity.js), and
// Pierre already kicks its own SoloInvitePill to bottom:190 for exactly
// this reason. A pill sitting on the ▼ button means the player cannot
// walk south — invisible on a desktop test, fatal on a phone.
// ⚠ THE POINTER LAW (2026-08-22) — TOUCH ALONE IS NOT "MOBILE".
// `navigator.maxTouchPoints > 0` is true on every touchscreen laptop and
// 2-in-1, so this said "phone" on caP'n's PC. In OrientationLock it was a
// hard lockout (the rotate curtain over a monitor that cannot rotate);
// here it is thumb controls and phone layout handed to someone with a
// mouse. `(pointer: coarse)` asks about the PRIMARY pointer instead of
// what the hardware merely supports: phone/tablet coarse, touchscreen PC
// fine. Touch is still required, so mouse-only desktops are unchanged.
// Full reasoning lives once, on isMobile() in OrientationLock.js.
const IS_TOUCH = (() => {
  if (typeof window === "undefined") return false;
  if (!(("ontouchstart" in window) || (navigator.maxTouchPoints || 0) > 0)) return false;
  try {
    if (typeof window.matchMedia === "function") return window.matchMedia("(pointer: coarse)").matches;
  } catch { /* fall back to the touch-only answer rather than throw */ }
  return true;
})();
const NARROW = () => typeof window !== "undefined" && window.innerWidth < 420;

// ════════════════════════════════════════════════════════════════════
// IESocialLayer — the whole thing
// ════════════════════════════════════════════════════════════════════
export function IESocialLayer({
  uid,
  name,
  game,                    // key of IE_GAMES
  zone = null,             // free text: which room inside this game
  partyId = null,          // threaded into invites so parties travel whole
  accent = "#a78bfa",      // ⟵ the one knob
  side = "right",          // "right" | "left"
  bottom = null,           // null = pick it from the device (see BOTTOM below)
  presence = true,         // a title with its own presence writer can opt out
  onTravel = null,         // override: (url, {partyId}) => void. Default: ieIdentity travel
  MarkView = null,         // ⟵ pass the title's <Mark/> to make every row openable
  hidden = false,          // ⟵ a title suppresses the whole layer during its own modals
  // ── DOCKING (2026-09-23, Terror's rail) — all optional, all off by default.
  // A title with a chrome band can seat the pill and panel INSIDE it
  // instead of floating them bottom-right. Omit every one of these and
  // the layer behaves exactly as it always has.
  open: openProp,          // ⟵ controlled open state (undefined = uncontrolled)
  onOpenChange = null,     // ⟵ (bool) => void, required when `open` is passed
  pillStyle = null,        // ⟵ style merged over the pill (position it)
  panelStyle = null,       // ⟵ style merged over the desktop panel (position it)
  hidePill = false,        // ⟵ hide the pill but keep toasts, presence, bridge
}) {
  const [openLocal, setOpenLocal] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? !!openProp : openLocal;
  const setOpen = useCallback((v) => {
    const next = typeof v === "function" ? v(open) : v;
    if (controlled) { if (onOpenChange && next !== open) onOpenChange(next); }
    else setOpenLocal(next);
  }, [controlled, onOpenChange, open]);
  const [markUid, setMarkUid] = useState(null);

  // ⚠ WITHOUT THIS THE PANEL IS HELVETICA. injectMarkChrome() is the
  // only thing in the Empire that appends the Google Fonts link, and it
  // was only ever called from Mark and MarkChip — so until a player
  // happened to open a Mark, every MK.display / MK.mono / MK.sans in
  // this file fell back to Georgia / Courier / Helvetica. A serif game
  // with a Helvetica friends panel bolted on is *literally* the
  // stitched-on look, and it was one missing call.
  injectMarkChrome();

  // ⚠ MEASURED AGAINST THE D-PAD. PlayerEntity's MobileDpad is 154px
  // tall at bottom:24 on touch devices, and Pierre already kicks its own
  // SoloInvitePill to bottom:190 for precisely this reason. A pill at
  // bottom:16 lands on the ▼ button — the player cannot walk SOUTH.
  // Invisible in every desktop test, fatal on a phone.
  const BOTTOM = bottom != null ? bottom : (IS_TOUCH ? 190 : 16);

  // A title suppresses the whole layer during its own modals — see
  // setSocialHidden in ieSocial.js.
  const globallyHidden = useSocialHidden();

  // Suppression must also CLOSE an open panel, or it springs back the
  // instant the title's modal dismisses.
  //
  // ⚠ THIS EFFECT BELONGS HERE AND NOWHERE ELSE. It was first written
  // into FriendsPanel — which has no `hidden`, no `globallyHidden` and
  // no `setOpen` in scope — so opening the panel threw
  // "hidden is not defined" on the first render. A parser cannot catch
  // that: every identifier is spelled correctly, they just live one
  // component up. Paste a hook, check its closure.
  useEffect(() => { if (hidden || globallyHidden) setOpen(false); },
            [hidden, globallyHidden]);

  useIEPresence({ uid, name, game, zone, enabled: presence && !!uid });
  useMarkIndex(uid);

  const F = useFriends({ uid, name, game, enabled: !!uid });

  // THE BRIDGE — the roster lives out here, the match code lives inside
  // <App/>. Published rather than threaded, for the reason in ieSocial's
  // own banner. Republished only when the pieces the game reads actually
  // change, never on every render.
  useEffect(() => {
    publishSocialBridge({ uid, myName: name, game, friends: F.friends, invite: F.invite, isOnline: F.isOnline });
    return () => publishSocialBridge(null);
  }, [uid, name, game, F.friends, F.invite, F.isOnline]);

  const go = useCallback((url, opts = {}) => {
    if (!url) return;
    if (onTravel) return onTravel(url, opts);
    try {
      const u = new URL(url, window.location.href);
      if (uid)         u.searchParams.set("uid", uid);
      if (opts.partyId) u.searchParams.set("partyId", opts.partyId);
      if (game)        u.searchParams.set("from", game);
      window.location.href = u.toString();
    } catch { window.location.href = url; }
  }, [uid, game, onTravel]);

  if (!uid) return null;
  const quiet = hidden || globallyHidden;

  return (
    <>
      {!quiet && !hidePill && <FriendsPill
        open={open} setOpen={setOpen}
        onlineCount={F.onlineCount}
        requestCount={F.requestsIn.length}
        accent={accent} side={side} bottom={BOTTOM}
        extraStyle={pillStyle}
      />}
      {!quiet && open && (
        <FriendsPanel
          F={F} uid={uid} game={game} partyId={partyId}
          accent={accent} side={side} bottom={BOTTOM}
          extraStyle={panelStyle}
          close={() => setOpen(false)} go={go}
          onOpenMark={MarkView ? setMarkUid : null}
        />
      )}
      {/* ⚠ TOASTS ARE NOT SUPPRESSED. Pierre's suppression predicate
          includes chat.open — correct for the pill, which would sit on
          the chat Send button, and wrong for an invite. A friend calling
          you to Terror while you are typing in zone chat is exactly the
          moment you are most socially engaged, and the toast would never
          render; F.invites is unread state, so it would reappear when
          chat closed, or TTL out first and vanish silently. */}
      <InviteToasts F={F} game={game} accent={accent} go={go} bottom={BOTTOM} />

      {/* ⚠ MarkView IS A PROP, NOT AN IMPORT, and that is THE EXCEPTION
          LAW doing real work. Mark.js needs useAchievements,
          achievements, SpriteManifest and AchievementBadgeBook — a stack
          only four titles carry today. A static import here would make
          the friends list refuse to BUILD in the other eight. Passed in,
          the four that have it get the full card and the rest get the
          nameplate, seal and code, which is most of THE GLANCE anyway.
          The day a title grows the stack it passes one more prop. */}
      {/* NO WRAPPER. Mark renders its own fixed, blurred backdrop at
          z-index 9000 and the Badge Book at 9500. A position:fixed div
          around it creates a stacking context that AMPUTATES both down
          to the wrapper's own z — the Mark would have competed at 940
          against Terror's StartMenu at 1100 and MarkEditor at 9100. */}
      {MarkView && markUid && (
        <MarkView uid={markUid} viewingSelf={markUid === uid}
                  onClose={() => setMarkUid(null)} />
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE PILL — always present, never in the way
// ════════════════════════════════════════════════════════════════════
// Canon (L3059) locks the party's Invite pill as "shown whenever
// currentPartyId is null, never hidden". The friends pill goes one
// further: never hidden at all. A social affordance that disappears
// when you have no friends is how a game with a friend system still
// feels like a game without one.
function FriendsPill({ open, setOpen, onlineCount, requestCount, accent, side, bottom, extraStyle = null }) {
  const [hot, setHot] = useState(false);
  return (
    <button
      onClick={() => setOpen(o => !o)}
      onMouseEnter={() => setHot(true)}
      onMouseLeave={() => setHot(false)}
      aria-label={`Friends — ${onlineCount} online${requestCount ? `, ${requestCount} pending` : ""}`}
      style={{
        position: "fixed", bottom, [side]: 14, zIndex: Z_PILL,
        display: "flex", alignItems: "center", gap: 7,
        padding: "8px 13px", cursor: "pointer",
        background: open ? accent : SD.ink,
        color: open ? SD.inkSolid : SD.text,
        border: `1px solid ${open ? accent : (hot ? SD.lineHot : SD.line)}`,
        clipPath: MK_CUT, fontFamily: SD.font, fontSize: 13.5,
        fontWeight: 700, letterSpacing: ".08em",
        boxShadow: hot || open ? `0 0 16px ${accent}55` : "0 2px 10px rgba(0,0,0,0.5)",
        transition: "background .15s, border-color .15s, box-shadow .15s",
        ...(extraStyle || {}),
      }}
    >
      <span style={{ fontSize: 14 }}>◈</span>
      <span>FRIENDS</span>
      {onlineCount > 0 && (
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          color: open ? SD.inkSolid : SD.green, fontVariantNumeric: "tabular-nums",
        }}>
          <span style={{
            width: 7, height: 7, borderRadius: 999,
            background: open ? SD.inkSolid : SD.green,
            boxShadow: open ? "none" : `0 0 6px ${SD.green}`,
          }} />
          {onlineCount}
        </span>
      )}
      {requestCount > 0 && <Badge n={requestCount} />}
    </button>
  );
}

const Badge = ({ n }) => (
  <span style={{
    minWidth: 17, height: 17, padding: "0 4px", borderRadius: 999,
    background: SD.gold, color: MK.void, fontSize: 11, fontWeight: 700,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    // mkPulse bottoms out at 35% opacity — it is the Mark's loading
    // whisper, where dim IS the intent. A pending-request badge must not
    // spend most of its cycle nearly invisible.
    animation: "mkBeat 2.2s ease-in-out infinite",
  }}>{n > 9 ? "9+" : n}</span>
);

// ════════════════════════════════════════════════════════════════════
// THE PANEL
// ════════════════════════════════════════════════════════════════════
function FriendsPanel({ F, uid, game, partyId, accent, side, bottom, close, go, onOpenMark, extraStyle = null }) {
  const [tab, setTab] = useState("roster");
  // Below 420px the panel already covered the whole screen; make that
  // deliberate — a bottom sheet with a scrim you can tap away — instead
  // of a 330px dropdown that happens to be as wide as the phone.
  // ⚠ BACKED BY A LISTENER. Read once during render, a rotated phone
  // keeps a 74dvh bottom sheet on a 360px-tall landscape viewport until
  // some unrelated state change reflows it mid-interaction — worse than
  // either steady state.
  const [sheet, setSheet] = useState(NARROW);
  useEffect(() => {
    const on = () => setSheet(NARROW());
    window.addEventListener("resize", on);
    window.addEventListener("orientationchange", on);
    return () => {
      window.removeEventListener("resize", on);
      window.removeEventListener("orientationchange", on);
    };
  }, []);

  const accentPair = useMemo(() =>
    Object.values(MARK_ACCENTS).find(
      a => a.hex.toLowerCase() === String(accent).toLowerCase()
    ) || { hex: accent, hi: MK.bone },
  [accent]);
  const [toast, setToast] = useState(null);

  // ⚠ ONE TIMER, HELD IN A REF. Uncancelled timeouts meant the first
  // action's timer cleared the SECOND action's toast — accept then
  // unfriend, and the confirmation flashed for under a second — and
  // both fired setState after the panel unmounted, which happens every
  // time the player closes it within 2.6s of doing anything.
  const toastTimer = useRef(null);
  const say = useCallback((msg, bad) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, bad: !!bad });
    // ⟵ DIAL. 2600 was measured in live QA and it is too short: the
    // strip carries a full sentence at 10px, and it expired before it
    // could be read.
    toastTimer.current = setTimeout(() => setToast(null), 3800);
  }, []);
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  return (
    <>
    {sheet && (
      <div onClick={close} style={{
        position: "fixed", inset: 0, zIndex: Z_PANEL - 1,
        background: "rgba(3,3,6,.6)", backdropFilter: "blur(3px)",
        animation: "mkFade .18s both",
      }} />
    )}
    <div style={{
      position: "fixed", zIndex: Z_PANEL,
      ...(sheet
        ? { left: 0, right: 0, bottom: 0, width: "100%", maxHeight: "74dvh" }
        : { bottom: bottom + 46, [side]: 14,
            width: "min(340px, calc(100vw - 28px))",
            maxHeight: "min(540px, calc(100dvh - 150px))",
            ...(extraStyle || {}) }),
      display: "flex", flexDirection: "column",
      background: SD.ink, color: SD.text,
      border: `1px solid ${MK.edge}`,
      // ⚠ NOT mkCut(0) on the sheet — a zero-inset clip-path is still a
      // clip-path, and it establishes a clipping context that would
      // amputate any position:fixed descendant.
      clipPath: sheet ? "none" : mkCut(14),
      // ⚠ NO `position` HERE. It was set twice in this one object literal
      // — "fixed" above, "relative" here — and last key wins, so the panel
      // silently became a relative div in normal document flow, rendered
      // from index.js as a sibling of <App/>, i.e. at the bottom of the
      // page. `fixed` is already a containing block for the absolute
      // TopRule and Sheen; nothing needed `relative` at all.
      fontFamily: SD.font, fontSize: 13,
      boxShadow: `0 22px 66px #000c, 0 0 34px ${accent}18`,
      backdropFilter: "blur(6px)",
      animation: "mkRise .22s cubic-bezier(.22,.61,.36,1) both",
      overflow: "hidden",
    }}>
      {/* The two things that make a Mark surface read as a CARD rather
          than a dropdown. Both pointer-events:none, both free. */}
      {/* Two-tone, not one. Mark's bar runs #d4a03c → #f6dfa0 and reads
          as lit metal; a single-hue bar reads as a web-app accent stripe.
          MARK_ACCENTS carries the `hi` for every accent the Empire has —
          which is the whole reason Pierre's sky/Tidelight was added to it
          rather than smuggled through as a raw hex. */}
      <TopRule accent={accentPair} />

      {/* ── my Mark: name, code, copy ───────────────────────────── */}
      <MyMark F={F} uid={uid} accent={accent} say={say} close={close} onOpenMark={onOpenMark} />

      {/* ── tabs ───────────────────────────────────────────────── */}
      <div style={{ display: "flex", borderBottom: `1px solid ${MK.edge}`, flexShrink: 0 }}>
        <Tab id="roster"   cur={tab} set={setTab} accent={accent}
             label={`ONLINE ${F.onlineCount}/${F.friends.length}`} />
        <Tab id="requests" cur={tab} set={setTab} accent={accent}
             label="REQUESTS" n={F.requestsIn.length} />
        <Tab id="add"      cur={tab} set={setTab} accent={accent} label="ADD" />
      </div>

      <div style={{ overflowY: "auto", flex: 1, minHeight: 92 }}>
        {tab === "roster"   && <Roster   F={F} game={game} partyId={partyId} accent={accent} say={say} go={go} onOpenMark={onOpenMark} />}
        {tab === "requests" && <Requests F={F} accent={accent} say={say} />}
        {tab === "add"      && <AddPanel F={F} accent={accent} say={say} />}
      </div>

      {toast && (
        <div style={{
          padding: "9px 12px", flexShrink: 0,
          background: toast.bad ? "rgba(192,20,39,0.18)" : "rgba(95,167,159,0.16)",
          color: toast.bad ? SD.red : SD.green,
          borderTop: `1px solid ${MK.edge}`,
        }}><Kicker size={10} tint={toast.bad ? SD.red : SD.green}>{toast.msg}</Kicker></div>
      )}
    </div>
    </>
  );
}

function MyMark({ F, uid, accent, say, close, onOpenMark }) {
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);
  useEffect(() => () => { if (copyTimer.current) clearTimeout(copyTimer.current); }, []);
  const copy = async () => {
    try { await navigator.clipboard.writeText(F.myCode); }
    catch { say("Copy blocked — read it out instead.", true); return; }
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div style={{
      position: "relative", overflow: "hidden",
      padding: "11px 12px", display: "flex", alignItems: "center", gap: 10,
      borderBottom: `1px solid ${MK.edge}`, flexShrink: 0,
      background: `linear-gradient(155deg, ${MK.slabHi}, ${MK.obsidian})`,
    }}>
      {/* A 105° sweep is a diagonal gleam across a 560x400 card and a
          near-vertical wipe across a 340x540 scrolling list. It belongs
          on the header plate, where the aspect is right and it does not
          re-wipe the roster every time the pill is tapped. */}
      <Sheen tint={accent} />
      {/* THE SEAL. Derived from the uid, unforgeable, and the one small
          thing on this panel that is nobody else's. */}
      <button
        onClick={() => onOpenMark?.(uid)}
        title={onOpenMark ? "Open your Mark" : F.myCode}
        style={{
          width: 34, height: 34, flexShrink: 0, padding: 0,
          display: "grid", placeItems: "center",
          background: `linear-gradient(155deg, ${MK.slab}, ${MK.void})`,
          border: `1px solid ${SEAL.hex}55`, clipPath: MK_CUT,
          color: SEAL.hi, fontFamily: MK.display, fontSize: 18,
          cursor: onOpenMark ? "pointer" : "default",
          boxShadow: `0 0 14px ${SEAL.hex}22`,
        }}>{markSeal(uid)}</button>

      <div style={{ flex: 1, minWidth: 0 }}>
        <Kicker size={8.5}>Your Mark</Kicker>
        <button onClick={copy} title="Copy your Mark code" style={{
          background: "none", border: "none", padding: 0, cursor: "pointer",
          color: copied ? SD.green : SD.goldHi, fontFamily: SD.mono,
          fontSize: 15, fontWeight: 700, letterSpacing: ".09em",
        }}>{F.myCode} <span style={{ fontSize: 10, color: SD.faint }}>{copied ? "✓" : "⧉"}</span></button>
      </div>

      <button onClick={close} aria-label="Close friends" style={{
        background: "none", border: `1px solid ${MK.edge}`, color: SD.dim,
        clipPath: MK_CUT, width: 40, height: 40, cursor: "pointer", fontSize: 14,
      }}>✕</button>
    </div>
  );
}

const Tab = ({ id, cur, set, label, n, accent }) => (
  <button onClick={() => set(id)} style={{
    flex: 1, padding: "13px 4px", cursor: "pointer",
    background: "transparent",
    border: "none", borderBottom: `2px solid ${cur === id ? accent : "transparent"}`,
    color: cur === id ? SD.text : SD.faint,
    fontFamily: SD.mono, fontSize: 9.5, letterSpacing: ".26em",
    textTransform: "uppercase",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
  }}>
    {label}{n > 0 && <Badge n={n} />}
  </button>
);

// ── THE ROSTER ──────────────────────────────────────────────────────
function Roster({ F, game, partyId, accent, say, go, onOpenMark }) {
  if (!F.friends.length) {
    return <Empty>
      Nobody yet. Hit <b style={{ color: accent }}>ADD</b> and trade codes —
      then you'll see each other from any game in the Empire.
    </Empty>;
  }
  return (
    <div>
      {F.friends.map(f => (
        <FriendRow key={f.uid} f={f} F={F} game={game} partyId={partyId}
                   accent={accent} say={say} go={go} onOpenMark={onOpenMark} />
      ))}
    </div>
  );
}

function FriendRow({ f, F, game, partyId, accent, say, go, onOpenMark }) {
  const [menu, setMenu] = useState(false);
  const here = f.online && f.game && f.game.id === game;
  const seal = markSeal(f.uid);

  const invite = async () => {
    const r = await F.invite(f.uid, { gameId: game, partyId });
    say(r.ok ? `Called ${f.name} over to ${r.label}.` : r.error, !r.ok);
  };
  const join = () => {
    if (!f.travelUrl) { say(`${f.game.label} isn't open yet.`, true); return; }
    go(f.travelUrl, { partyId });
  };

  return (
    <>
    <div style={{
      padding: "8px 12px",
      borderBottom: menu ? "none" : `1px solid ${MK.edge}66`,
      display: "flex", alignItems: "center", gap: 9,
      background: here ? `linear-gradient(90deg, ${MK.verd}14, transparent)` : "transparent",
    }}>
      {/* seal + presence, one glyph. The dot rides the corner of the
          nameplate rather than sitting beside it — one thing to read. */}
      <button
        onClick={() => onOpenMark?.(f.uid)}
        title={onOpenMark ? `Open ${f.name}'s Mark` : f.code}
        style={{
          position: "relative", width: 30, height: 30, flexShrink: 0, padding: 0,
          display: "grid", placeItems: "center",
          background: `linear-gradient(155deg, ${MK.slab}, ${MK.void})`,
          border: `1px solid ${f.online ? `${SEAL.hex}66` : MK.edge}`,
          clipPath: MK_CUT, cursor: onOpenMark ? "pointer" : "default",
          color: f.online ? SEAL.hi : SD.faint,
          fontFamily: MK.display, fontSize: 15,
          opacity: f.online ? 1 : 0.6,
        }}>
        {seal}
        <span style={{
          position: "absolute", right: -2, bottom: -2,
          width: 7, height: 7, borderRadius: 999,
          background: f.online ? SD.green : MK.smoke,
          boxShadow: f.online ? `0 0 7px ${SD.green}` : "none",
          border: `1.5px solid ${MK.obsidian}`,
        }} />
      </button>

      <div style={{ flex: 1, minWidth: 0, opacity: f.online ? 1 : 0.62 }}>
        <div style={{
          fontFamily: MK.display, fontWeight: 600, fontSize: 16.5,
          letterSpacing: ".01em", lineHeight: 1.2,
          color: SD.text, whiteSpace: "nowrap",
          overflow: "hidden", textOverflow: "ellipsis",
        }}>{f.name}</div>
        <div style={{ fontSize: 10.5, color: SD.dim, display: "flex", alignItems: "center", gap: 4 }}>
          {f.online ? (
            <>
              <span style={{ color: f.game.color, fontFamily: MK.display }}>{f.game.glyph}</span>
              <span style={{ color: f.game.color, fontFamily: SD.font, letterSpacing: ".05em" }}>
                {f.game.label}
              </span>
              {f.zone && <span style={{ color: SD.faint }}>· {f.zone}</span>}
            </>
          ) : (
            <Kicker size={9}>{f.code}</Kicker>
          )}
        </div>
      </div>

      {/* THE TWO VERBS. "Come here" and "I'm coming" — a friends list
          that can only show you where someone is, is a phone book. */}
      {f.online && !here && (
        <>
          <MiniBtn onClick={invite} accent={accent} title={`Invite to ${gameMeta(game).label}`}>CALL</MiniBtn>
          <MiniBtn onClick={join} accent={accent} title={`Go to ${f.game.label}`} solid>GO</MiniBtn>
        </>
      )}
      {here && (
        <Kicker size={8.5} tint={SD.green}>Together</Kicker>
      )}

      <MiniBtn onClick={() => setMenu(m => !m)} accent={accent}
               title={menu ? "Close" : "More"}>{menu ? "✕" : "⋯"}</MiniBtn>
      </div>

      {/* ⚠ AN INLINE DRAWER, NOT AN ABSOLUTE DROPDOWN. The first version
          opened a menu at `bottom: 115%` inside a panel with
          `overflow: hidden` — so for any row past the first it was
          CLIPPED BY ITS OWN PARENT, and escaping that would have meant a
          portal and measured anchoring for four buttons. A row that
          expands in place cannot be clipped, cannot be mispositioned,
          needs no click-away catcher, and gives every action a full-width
          40px target instead of a 26px one. */}
      {menu && (
        <div style={{
          display: "flex", gap: 6, padding: "0 12px 9px 51px",
          borderBottom: `1px solid ${MK.edge}66`, background: `${MK.void}66`,
        }}>
          {onOpenMark && (
            <DrawerBtn onClick={() => { setMenu(false); onOpenMark(f.uid); }} accent={accent}>
              Mark
            </DrawerBtn>
          )}
          {/* THE TITLE'S OWN VERBS — see setFriendActions in ieSocial. */}
          {friendActionsFor(f).map((a) => (
            <DrawerBtn key={a.label} accent={accent} onClick={() => { setMenu(false); Promise.resolve().then(() => a.run(f)).catch((e) => say(e?.message || "That didn't work.", true)); }}>
              {a.label}
            </DrawerBtn>
          ))}
          <DrawerBtn accent={accent} onClick={async () => {
            setMenu(false);
            try { await navigator.clipboard.writeText(f.code); say(`Copied ${f.name}'s code.`); }
            catch { say(f.code); }
          }}>Copy</DrawerBtn>
          <DrawerBtn accent={SD.dim} onClick={async () => {
            setMenu(false);
            const r = await F.removeFriend(f.uid);
            say(r.ok ? `Removed ${f.name}.` : (r.error || "Could not do that."), !r.ok);
          }}>Drop</DrawerBtn>
          <DrawerBtn accent={SD.red} onClick={async () => {
            setMenu(false);
            const r = await F.block(f.uid, f.name);
            say(r.ok ? `Blocked ${f.name}.` : (r.error || "Could not block."), !r.ok);
          }}>Block</DrawerBtn>
        </div>
      )}
    </>
  );
}

const DrawerBtn = ({ children, onClick, accent }) => (
  <button onClick={onClick} style={{
    // 40, like every other target here. And each label is ONE word:
    // four flex:1 buttons in a 340px panel get ~65px, and "COPY CODE" at
    // mono 9 / .14em needs 72 — it was silently clipped by the panel.
    flex: 1, height: 40, padding: "0 4px", cursor: "pointer",
    background: "transparent", color: accent,
    border: `1px solid ${accent}55`, clipPath: MK_CUT,
    fontFamily: MK.mono, fontSize: 8.5, letterSpacing: ".1em",
    textTransform: "uppercase", whiteSpace: "nowrap",
  }}>{children}</button>
);

// ── REQUESTS ────────────────────────────────────────────────────────
function Requests({ F, accent, say }) {
  const { requestsIn, requestsOut, blocked } = F;
  if (!requestsIn.length && !requestsOut.length && !blocked.length) {
    return <Empty>No requests. Quiet out there.</Empty>;
  }
  return (
    <div>
      {requestsIn.map(r => (
        <div key={r.uid} style={rowStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: MK.display, fontWeight: 600, fontSize: 16, color: SD.text }}>{r.name}</div>
            <div style={{ fontSize: 11, color: SD.dim, fontFamily: SD.mono }}>{r.code}</div>
          </div>
          <MiniBtn solid accent={SD.green} title="Accept" onClick={async () => {
            const x = await F.accept(r.uid);
            say(x.ok ? `${r.name} is a friend now.` : x.error, !x.ok);
          }}>✓</MiniBtn>
          <MiniBtn accent={SD.red} title="Decline" onClick={async () => {
            await F.decline(r.uid); say(`Declined ${r.name}.`);
          }}>✕</MiniBtn>
        </div>
      ))}
      {requestsOut.map(r => (
        <div key={r.uid} style={{ ...rowStyle, opacity: 0.6 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: MK.display, fontWeight: 600, fontSize: 16, color: SD.text }}>{r.name}</div>
            <div style={{ fontSize: 11, color: SD.dim }}>waiting on them…</div>
          </div>
          <MiniBtn accent={SD.red} title="Cancel" onClick={async () => {
            await F.removeFriend(r.uid); say("Request pulled back.");
          }}>✕</MiniBtn>
        </div>
      ))}
      {blocked.map(r => (
        <div key={r.uid} style={{ ...rowStyle, opacity: 0.5 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: MK.display, fontWeight: 600, fontSize: 16, color: SD.text }}>{r.name}</div>
            <div style={{ fontSize: 11, color: SD.red }}>blocked</div>
          </div>
          <MiniBtn accent={accent} title="Unblock" onClick={async () => {
            await F.unblock(r.uid); say(`Unblocked ${r.name}.`);
          }}>↺</MiniBtn>
        </div>
      ))}
    </div>
  );
}

// ── ADD ─────────────────────────────────────────────────────────────
function AddPanel({ F, accent, say }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    const r = await F.addByCode(code);
    setBusy(false);
    if (r.ok) { setCode(""); say(`Request sent to ${r.name || "them"}.`); }
    else say(r.error, true);
  };

  return (
    <form onSubmit={submit} style={{ padding: 12 }}>
      <div style={{ fontSize: 11.5, color: SD.dim, lineHeight: 1.55, marginBottom: 9 }}>
        Every soul carries a code. Trade yours, type theirs — you'll be on
        each other's list in <b style={{ color: SD.text }}>every game in the Empire</b>,
        not just this one.
      </div>
      <input
        value={code}
        onChange={e => setCode(e.target.value)}
        placeholder="MK-XXXX-XXXX"
        spellCheck={false} autoCapitalize="characters" autoComplete="off"
        style={{
          width: "100%", boxSizing: "border-box", padding: "9px 11px",
          background: MK.void, color: SD.text,
          border: `1px solid ${MK.edge}`, clipPath: MK_CUT,
          fontFamily: SD.mono, fontSize: 15, letterSpacing: ".14em",
          textTransform: "uppercase", outline: "none",
        }}
      />
      <button type="submit" disabled={busy} style={{
        width: "100%", marginTop: 8, padding: "9px 0", cursor: busy ? "wait" : "pointer",
        background: accent, color: MK.void, border: "none", clipPath: MK_CUT,
        fontFamily: SD.mono, fontWeight: 600, fontSize: 10.5, letterSpacing: ".2em",
        opacity: busy ? 0.6 : 1,
      }}>{busy ? "SENDING…" : "SEND REQUEST"}</button>
    </form>
  );
}

// ════════════════════════════════════════════════════════════════════
// INVITE TOASTS — "come over here"
// ════════════════════════════════════════════════════════════════════
// Top-centre, above everything, and they stack. The single-slot
// partyInvites node this supersedes could only ever show one; two
// friends calling you at once meant one of them silently lost.
function InviteToasts({ F, game, accent, go, bottom = 16 }) {
  // ⚠ NEVER CENTRED AT THE TOP. That is the one lane Pierre already uses
  // for BOTH of its own banners — the party invite at top:58 and the
  // invite flash at top:110 — and at 360px a centred 336px toast covers
  // the Crest wallet and the log-out button completely. Right-anchored
  // below the wallet on a wide screen; a bottom stack above the pill on
  // a narrow one, where the top edge is already full.
  const stack = typeof window !== "undefined" && window.innerWidth < 900;
  if (!F.invites.length) return null;
  return (
    <div style={{
      position: "fixed", zIndex: Z_TOAST,
      ...(stack
        ? { left: 12, right: 12, bottom: bottom + 54 }
        : { top: 106, right: 14, width: "min(340px, calc(100vw - 28px))" }),
      display: "flex", flexDirection: stack ? "column-reverse" : "column", gap: 8,
    }}>
      {F.invites.slice(0, 3).map(inv => {
        const m = gameMeta(inv.gameId);
        return (
          <div key={inv.fromUid} style={{
            background: `linear-gradient(155deg, ${MK.slabHi}, ${MK.obsidian})`,
            border: `1px solid ${m.color}88`, clipPath: MK_CUT,
            padding: "10px 12px", display: "flex", alignItems: "center", gap: 10,
            fontFamily: SD.font, color: SD.text, fontSize: 12.5,
            boxShadow: `0 6px 24px rgba(0,0,0,0.6), 0 0 18px ${m.color}33`,
            animation: "mkDrop .22s cubic-bezier(.22,.61,.36,1) both",
          }}>
            <span style={{ fontSize: 21, fontFamily: MK.display, color: m.color }}>{m.glyph}</span>
            <div style={{ flex: 1, minWidth: 0, lineHeight: 1.35 }}>
              <b>{inv.fromName || "A friend"}</b> wants you in{" "}
              <span style={{ color: m.color, fontWeight: 700 }}>{inv.label || m.label}</span>
              {matchCodeOf(inv) && (
                <div style={{ color: m.color, fontSize: 11, fontFamily: MK.mono, letterSpacing: ".12em" }}>
                  ◆ SOLREND {matchCodeOf(inv)}
                </div>
              )}
              {inviteNoteOf(inv) && <div style={{ color: SD.dim, fontSize: 11 }}>{inviteNoteOf(inv)}</div>}
            </div>
            {/* ⭐ JOIN NOW ACTUALLY JOINS (2026-09-05). This was one line —
                `go(inv.url, ...)` — a page navigation and nothing else. It
                never touched a match, so "join my duel" meant "open the
                game's front door and find each other by voice". If the
                invite carries a seat AND we are already standing in that
                title, the registered handler seats us without a reload;
                otherwise we travel, and the destination reads `?match=`
                off its own URL on arrival. Travelling is also wrong on
                localhost, where `url` is the allowlisted production
                domain and would throw a dev clean off their own build. */}
            <button onClick={() => {
                const mc = matchCodeOf(inv);
                const handler = matchInviteHandler();
                F.clearInvite(inv.fromUid);
                // The title looks first (a trade offer, say) — see setInviteOpener.
                const opener = inviteOpener();
                if (opener && inv.gameId === game) { let took = false; try { took = !!opener(inv); } catch (e) {} if (took) return; }
                if (mc && handler && inv.gameId === game) { handler(mc, inv); return; }
                go(inv.url, { partyId: inv.partyId });
              }}
              style={{
                background: m.color, color: MK.void, border: "none",
                clipPath: MK_CUT, padding: "7px 13px", cursor: "pointer",
                fontFamily: MK.mono, fontWeight: 700, fontSize: 10.5, letterSpacing: ".2em",
              }}>JOIN</button>
            <button onClick={() => F.clearInvite(inv.fromUid)} aria-label="Dismiss"
              style={{
                background: "none", border: `1px solid ${MK.edge}`, color: SD.dim,
                clipPath: MK_CUT, width: 40, height: 40, cursor: "pointer",
              }}>✕</button>
          </div>
        );
      })}
    </div>
  );
}

// ── small parts ─────────────────────────────────────────────────────
// Both lists in this panel are 56px rows: a 40px target plus 16px of
// padding. They disagreed by two pixels — the kind of thing nobody can
// name and everybody can feel.
const rowStyle = {
  padding: "8px 12px", borderBottom: `1px solid ${MK.edge}66`,
  display: "flex", alignItems: "center", gap: 8,
};

const Empty = ({ children }) => (
  <div style={{ padding: "22px 16px", color: SD.dim, fontSize: 12, lineHeight: 1.6, textAlign: "center" }}>
    {children}
  </div>
);

const MiniBtn = ({ children, onClick, accent, title, solid }) => (
  <button onClick={onClick} title={title} aria-label={title} style={{
    background: solid ? accent : "transparent",
    color: solid ? MK.void : accent,
    border: `1px solid ${accent}${solid ? "" : "77"}`,
    clipPath: MK_CUT, minWidth: 40, height: 40, padding: "0 9px",
    cursor: "pointer", fontFamily: MK.mono, fontSize: 9, fontWeight: 600,
    letterSpacing: ".14em", textTransform: "uppercase",
    flexShrink: 0, lineHeight: 1,
  }}>{children}</button>
);

// Keyframes come from injectMarkChrome() in markData.js — mkRise,
// mkFade, mkSheen and mkPulse, the same four the Mark itself uses.
// A second private set is how two surfaces drift apart.

// ════════════════════════════════════════════════════════════════════
// IESocialDock — THE ACTUAL ONE-LINE MOUNT
// ════════════════════════════════════════════════════════════════════
// IESocialLayer above needs uid and name passed in, which assumes the
// title has a single component that knows both AND wraps every screen.
// Terror's App.js does not — it is nineteen early returns, and there is
// no one place to hang a layer from. That is the normal shape of a
// mature IE title, not the exception.
//
// So the dock identifies ITSELF. It sits beside <App/> in index.js,
// reads the shared session through useIEAuth, and follows the player
// across every screen the title has, forever, with no prop threading
// and no refactor of anybody's render tree:
//
//   // index.js
//   import IESocialDock from "./IEFriends";
//   root.render(<>
//     <App />
//     <IESocialDock game="terror" />
//   </>);
//
// It renders nothing at all until somebody is signed in, so a title
// that mounts it before it has accounts pays exactly zero.
//
// A title that already has uid and name in hand at a wrapping
// component should use <IESocialLayer/> directly and skip the second
// auth listener. Both are supported on purpose — see THE EXCEPTION LAW.
export default function IESocialDock({ game, zone = null, accent, side, bottom, partyId = null, MarkView = null, hidden = false, open, onOpenChange = null, pillStyle = null, panelStyle = null, hidePill = false }) {
  const A = useIEAuth({ gameId: game });

  // THE GUEST GATE. A player carried here on a handoff can read their
  // soul but cannot write — and a friends list that cannot write is a
  // list that shows requests it can never accept. Better to show
  // nothing than to show a broken thing.
  //
  // "ready" and not merely "has a uid": between signing in and the
  // profile resolving, name is the "Unclaimed" fallback — and mounting
  // one tick early publishes THAT to every friend as this player's
  // presence name. On a flaky connection, which holds phase at "boot"
  // and retries, they would sit in everyone's roster as Unclaimed for
  // as long as it took.
  if (A.phase !== "ready") return null;

  return (
    <IESocialLayer
      uid={A.authUid}
      name={A.name}
      game={game}
      zone={zone}
      partyId={partyId || A.handoffPartyId}
      accent={accent}
      side={side}
      bottom={bottom}
      MarkView={MarkView}
      hidden={hidden}
      open={open} onOpenChange={onOpenChange}
      pillStyle={pillStyle} panelStyle={panelStyle} hidePill={hidePill}
      onTravel={A.travel}   /* already a stable useCallback — wrapping it inline
                               would rebuild `go` on every render and defeat every
                               memo below it. */
    />
  );
}

// IESocialLayer and IESocialDock are already exported at their declarations.
export { FriendsPill, FriendsPanel, InviteToasts };
